/**
 * Preset MCP server templates for the "Add MCP server" dialog. Each entry
 * prefills the connection form with a hosted Streamable HTTP endpoint and an
 * Authorization: Bearer token header.
 *
 * Templates carry an optional `authMode`: omitted/"static" templates prefill an
 * Authorization: Bearer (or custom) header; "oauth" templates (Notion, Slack,
 * Sentry, Atlassian, custom-oauth) drive the server-side MCP OAuth flow instead
 * and need no manual header. OAuth templates with `requiresClient: true` (Slack)
 * require the user to supply a pre-registered client_id/client_secret.
 *
 * URLs and auth requirements verified against official provider docs as of 2026:
 * - GitHub: https://github.com/github/github-mcp-server/blob/main/docs/remote-server.md (PAT as Bearer)
 * - Linear: https://linear.app/docs/mcp (API key / OAuth token as Bearer)
 * - Supabase: https://supabase.com/docs/guides/getting-started/mcp (PAT as Bearer; needs project_ref)
 * - Stripe: https://docs.stripe.com/mcp (restricted API key as Bearer)
 * - Cloudflare: https://developers.cloudflare.com/agents/model-context-protocol/cloudflare/servers-for-cloudflare/ (API token as Bearer)
 * - Hugging Face: https://huggingface.co/docs/hub/agents-mcp (HF access token as Bearer)
 * - Context7: https://context7.com/docs/resources/all-clients (API key as Bearer; also accepts CONTEXT7_API_KEY header)
 * - Exa: https://exa.ai/docs/reference/exa-mcp (API key as Bearer or x-api-key)
 * - Firecrawl: https://docs.firecrawl.dev/mcp-server (API key as Bearer on the /v2/mcp endpoint)
 * - Tavily: https://docs.tavily.com/documentation/mcp (API key as Bearer)
 * - PostHog: https://posthog.com/docs/model-context-protocol/faq (personal API key as Bearer)
 * - monday.com: https://developer.monday.com/api-reference/docs/monday-mcp-overview (personal API token as Bearer)
 * - Airtable: https://support.airtable.com/docs/using-the-airtable-mcp-server (personal access token as Bearer)
 * - Zapier: https://help.zapier.com/hc/en-us/articles/36265392843917 (per-user server URL carries auth; no header needed)
 * - Neon: https://neon.com/docs/ai/neon-mcp-server (Neon API key as Bearer)
 * - Jina: https://github.com/jina-ai/MCP (Jina API key as Bearer; optional for read-only tools)
 * - DeepWiki: https://docs.devin.ai/work-with-devin/deepwiki-mcp (no auth; public GitHub repos)
 * - LangSmith: https://docs.langchain.com/langsmith/langsmith-mcp-server (LANGSMITH-API-KEY header)
 * - Semgrep: https://github.com/semgrep/mcp (no auth on hosted mcp.semgrep.ai)
 * - Apify: https://docs.apify.com/platform/integrations/mcp (Apify API token as Bearer)
 * - Browserbase: https://docs.browserbase.com/integrations/mcp/setup (browserbaseApiKey query param)
 * - Search1API: https://github.com/fatwang2/search1api-mcp (Search1API key as Bearer)
 * - Datadog: https://docs.datadoghq.com/mcp_server/setup (DD_API_KEY + DD_APPLICATION_KEY headers)
 * - Honeycomb: https://docs.honeycomb.io/integrations/mcp/configuration-guide (Bearer KEY_ID:SECRET_KEY)
 */

import { headerViewToRow } from "./McpHeadersField";

export const MCP_TEMPLATE_CATEGORIES = [
  { id: "development", label: "Development" },
  { id: "collaboration", label: "Collaboration" },
  { id: "data", label: "Data" },
  { id: "business", label: "Business" },
  { id: "infrastructure", label: "Infrastructure" },
];

export const MCP_SERVER_TEMPLATES = [
  {
    id: "github",
    title: "GitHub",
    shortDescription:
      "Manage repositories, issues, pull requests, and Actions from your agents.",
    category: "development",
    tags: ["repos", "issues", "pull requests"],
    featured: true,
    integrations: ["Issues", "PRs", "Repos"],
    prefilled: {
      name: "GitHub",
      url: "https://api.githubcopilot.com/mcp/",
      headers: [{ name: "Authorization", valuePrefix: "Bearer ", secret: true }],
    },
    authNote:
      "Paste a GitHub Personal Access Token (classic or fine-grained) with the scopes your agents need (e.g. repo, workflow, read:org). It's sent as the Authorization: Bearer token.",
    docsUrl:
      "https://github.com/github/github-mcp-server/blob/main/docs/remote-server.md",
  },
  {
    id: "linear",
    title: "Linear",
    shortDescription:
      "Find, create, and update issues, projects, and comments in Linear.",
    category: "development",
    tags: ["issues", "projects", "roadmaps"],
    featured: true,
    integrations: ["Issues", "Projects", "Comments"],
    prefilled: {
      name: "Linear",
      url: "https://mcp.linear.app/mcp",
      headers: [{ name: "Authorization", valuePrefix: "Bearer ", secret: true }],
    },
    authNote:
      "Paste a Linear personal API key (Settings → Account → Security & Access) or an OAuth access token as the Bearer secret. Linear's hosted MCP accepts both directly in the Authorization header.",
    docsUrl: "https://linear.app/docs/mcp",
  },
  {
    id: "supabase",
    title: "Supabase",
    shortDescription:
      "Query Postgres, manage auth, storage, and realtime resources.",
    category: "data",
    tags: ["postgres", "auth", "storage"],
    featured: false,
    integrations: ["Database", "Auth", "Storage"],
    prefilled: {
      name: "Supabase",
      url: "https://mcp.supabase.com/mcp?project_ref=",
      headers: [{ name: "Authorization", valuePrefix: "Bearer ", secret: true }],
    },
    authNote:
      "Append your project ref to the URL (…?project_ref=YOUR_PROJECT_REF), then paste a Supabase personal access token (Account → Access Tokens) as the Bearer secret.",
    docsUrl: "https://supabase.com/docs/guides/getting-started/mcp",
  },
  {
    id: "stripe",
    title: "Stripe",
    shortDescription:
      "Look up customers, payments, subscriptions, and invoices.",
    category: "business",
    tags: ["payments", "billing", "customers"],
    featured: false,
    integrations: ["Payments", "Subscriptions", "Invoices"],
    prefilled: {
      name: "Stripe",
      url: "https://mcp.stripe.com",
      headers: [{ name: "Authorization", valuePrefix: "Bearer ", secret: true }],
    },
    authNote:
      "Paste a Stripe restricted API key (rk_…) with only the permissions your agents need as the Bearer secret. Stripe's hosted MCP accepts restricted keys directly in the Authorization header.",
    docsUrl: "https://docs.stripe.com/mcp",
  },
  {
    id: "cloudflare",
    title: "Cloudflare",
    shortDescription:
      "Manage Workers, KV, R2, DNS, and other Cloudflare resources.",
    category: "infrastructure",
    tags: ["workers", "dns", "edge"],
    featured: false,
    integrations: ["Workers", "KV", "R2"],
    prefilled: {
      name: "Cloudflare",
      url: "https://mcp.cloudflare.com/mcp",
      headers: [{ name: "Authorization", valuePrefix: "Bearer ", secret: true }],
    },
    authNote:
      "Paste a Cloudflare API token (user or account token) with the scopes required for the tools you want agents to use. It's sent as the Authorization: Bearer token.",
    docsUrl:
      "https://developers.cloudflare.com/agents/model-context-protocol/cloudflare/servers-for-cloudflare/",
  },
  {
    id: "notion",
    title: "Notion",
    shortDescription:
      "Search, read, and update pages, databases, and comments in Notion.",
    category: "collaboration",
    tags: ["pages", "databases", "docs"],
    featured: true,
    authMode: "oauth",
    integrations: ["Pages", "Databases", "Comments"],
    prefilled: {
      name: "Notion",
      url: "https://mcp.notion.com/mcp",
      headers: [],
    },
    authNote:
      "Notion's hosted MCP is OAuth-only. Click Connect and authorize Workflow86 in the popup — no token or header needed. All agents in your workspace act as the authorizing account.",
    docsUrl: "https://developers.notion.com/docs/mcp",
  },
  {
    id: "slack",
    title: "Slack",
    shortDescription:
      "Read and post messages, search channels, and manage Slack conversations.",
    category: "collaboration",
    tags: ["messages", "channels", "chat"],
    featured: false,
    authMode: "oauth",
    requiresClient: true,
    integrations: ["Messages", "Channels", "Search"],
    prefilled: {
      name: "Slack",
      url: "https://mcp.slack.com/mcp",
      headers: [],
    },
    authNote:
      "Slack's hosted MCP requires your own Slack OAuth app. Create one at api.slack.com/apps, add the displayed callback URL to its Redirect URLs, then paste the app's Client ID and Client Secret here and click Connect.",
    docsUrl: "https://api.slack.com/",
  },
  {
    id: "sentry",
    title: "Sentry",
    shortDescription:
      "Investigate issues, errors, and performance data across your Sentry projects.",
    category: "development",
    tags: ["errors", "issues", "monitoring"],
    featured: false,
    authMode: "oauth",
    integrations: ["Issues", "Errors", "Releases"],
    prefilled: {
      name: "Sentry",
      url: "https://mcp.sentry.dev/mcp",
      headers: [],
    },
    authNote:
      "Sentry's hosted MCP is OAuth-only. Click Connect and authorize Workflow86 in the popup — no token or header needed.",
    docsUrl: "https://docs.sentry.io/product/sentry-mcp/",
  },
  {
    id: "atlassian",
    title: "Atlassian",
    shortDescription:
      "Work with Jira issues and Confluence pages across your Atlassian sites.",
    category: "collaboration",
    tags: ["jira", "confluence", "issues"],
    featured: false,
    authMode: "oauth",
    integrations: ["Jira", "Confluence", "Issues"],
    prefilled: {
      name: "Atlassian",
      url: "https://mcp.atlassian.com/v1/mcp",
      headers: [],
    },
    authNote:
      "Atlassian's hosted MCP is OAuth-only. Click Connect and authorize Workflow86 in the popup — no token or header needed.",
    docsUrl: "https://www.atlassian.com/platform/remote-mcp-server",
  },
  {
    id: "custom-oauth",
    title: "Custom OAuth server",
    shortDescription:
      "Connect any OAuth-protected Streamable HTTP MCP endpoint via the OAuth flow.",
    category: "infrastructure",
    tags: ["custom", "oauth", "self-hosted"],
    featured: false,
    authMode: "oauth",
    requiresClient: true,
    integrations: ["Any OAuth MCP server"],
    prefilled: {
      name: "",
      url: "",
      headers: [],
    },
    authNote:
      "Enter the Streamable HTTP URL of an OAuth-protected MCP server, then paste a Client ID and Client Secret from an OAuth app you registered with that server. Add the callback URL shown below to the app's allowed redirect URIs.",
    docsUrl: "https://modelcontextprotocol.io/specification/draft/basic/authorization",
  },
  {
    id: "huggingface",
    title: "Hugging Face",
    shortDescription:
      "Search models, datasets, and Spaces, and run inference on the Hub.",
    category: "data",
    tags: ["models", "datasets", "inference"],
    featured: true,
    integrations: ["Models", "Datasets", "Spaces"],
    prefilled: {
      name: "Hugging Face",
      url: "https://huggingface.co/mcp",
      headers: [{ name: "Authorization", valuePrefix: "Bearer ", secret: true }],
    },
    authNote:
      "Paste a Hugging Face access token (Settings → Access Tokens) as the Bearer secret. Use a read token to browse, or a write token to manage Spaces. It's sent as the Authorization: Bearer token.",
    docsUrl: "https://huggingface.co/docs/hub/agents-mcp",
  },
  {
    id: "context7",
    title: "Context7",
    shortDescription:
      "Pull up-to-date, version-specific docs and code examples for any library.",
    category: "development",
    tags: ["docs", "libraries", "code"],
    featured: false,
    integrations: ["Docs", "Libraries", "Examples"],
    prefilled: {
      name: "Context7",
      url: "https://mcp.context7.com/mcp",
      headers: [{ name: "Authorization", valuePrefix: "Bearer ", secret: true }],
    },
    authNote:
      "Paste a Context7 API key (context7.com/dashboard, starts with ctx7sk) as the Bearer secret. Context7's hosted MCP accepts the key directly in the Authorization header.",
    docsUrl: "https://context7.com/docs/resources/all-clients",
  },
  {
    id: "exa",
    title: "Exa",
    shortDescription:
      "Run AI-native web search and fetch clean page content for your agents.",
    category: "data",
    tags: ["web search", "research", "retrieval"],
    featured: false,
    integrations: ["Web search", "Web fetch", "Research"],
    prefilled: {
      name: "Exa",
      url: "https://mcp.exa.ai/mcp",
      headers: [{ name: "Authorization", valuePrefix: "Bearer ", secret: true }],
    },
    authNote:
      "Paste an Exa API key (dashboard.exa.ai) as the Bearer secret to lift the free-tier rate limits. Exa's hosted MCP accepts the key in the Authorization header.",
    docsUrl: "https://exa.ai/docs/reference/exa-mcp",
  },
  {
    id: "firecrawl",
    title: "Firecrawl",
    shortDescription:
      "Scrape, crawl, map, and extract structured data from any website.",
    category: "data",
    tags: ["scraping", "crawling", "extraction"],
    featured: false,
    integrations: ["Scrape", "Crawl", "Extract"],
    prefilled: {
      name: "Firecrawl",
      url: "https://mcp.firecrawl.dev/v2/mcp",
      headers: [{ name: "Authorization", valuePrefix: "Bearer ", secret: true }],
    },
    authNote:
      "Paste a Firecrawl API key (firecrawl.dev/app/api-keys, starts with fc-) as the Bearer secret. Use this header form rather than putting the key in the URL so it's never logged.",
    docsUrl: "https://docs.firecrawl.dev/mcp-server",
  },
  {
    id: "tavily",
    title: "Tavily",
    shortDescription:
      "Real-time web search, extract, map, and crawl tuned for AI agents.",
    category: "data",
    tags: ["web search", "extract", "crawl"],
    featured: false,
    integrations: ["Search", "Extract", "Crawl"],
    prefilled: {
      name: "Tavily",
      url: "https://mcp.tavily.com/mcp/",
      headers: [{ name: "Authorization", valuePrefix: "Bearer ", secret: true }],
    },
    authNote:
      "Paste a Tavily API key (app.tavily.com, starts with tvly-) as the Bearer secret. Tavily's hosted MCP accepts the key in the Authorization header.",
    docsUrl: "https://docs.tavily.com/documentation/mcp",
  },
  {
    id: "posthog",
    title: "PostHog",
    shortDescription:
      "Query analytics, manage feature flags, and dig into errors and insights.",
    category: "business",
    tags: ["analytics", "feature flags", "insights"],
    featured: false,
    integrations: ["Analytics", "Feature flags", "Errors"],
    prefilled: {
      name: "PostHog",
      url: "https://mcp.posthog.com/mcp",
      headers: [{ name: "Authorization", valuePrefix: "Bearer ", secret: true }],
    },
    authNote:
      "Create a personal API key with the 'MCP Server' preset (PostHog → Settings → Personal API keys), which scopes access to one project. Paste it (starts with phx_) as the Bearer secret.",
    docsUrl: "https://posthog.com/docs/model-context-protocol/faq",
  },
  {
    id: "monday",
    title: "monday.com",
    shortDescription:
      "Read and write boards, items, columns, updates, and users in monday.com.",
    category: "business",
    tags: ["boards", "items", "work management"],
    featured: true,
    integrations: ["Boards", "Items", "Updates"],
    prefilled: {
      name: "monday.com",
      url: "https://mcp.monday.com/mcp",
      headers: [{ name: "Authorization", valuePrefix: "Bearer ", secret: true }],
    },
    authNote:
      "Paste a monday.com personal API token (avatar → Developers → My Access Tokens, or Admin → API) as the Bearer secret. monday's hosted Platform MCP accepts a personal token directly in the Authorization header.",
    docsUrl:
      "https://developer.monday.com/api-reference/docs/monday-mcp-overview",
  },
  {
    id: "airtable",
    title: "Airtable",
    shortDescription:
      "Read and write records, fields, tables, and comments across your bases.",
    category: "data",
    tags: ["records", "bases", "tables"],
    featured: false,
    integrations: ["Records", "Bases", "Comments"],
    prefilled: {
      name: "Airtable",
      url: "https://mcp.airtable.com/mcp",
      headers: [{ name: "Authorization", valuePrefix: "Bearer ", secret: true }],
    },
    authNote:
      "Create a personal access token at airtable.com/create/tokens with the scopes your agents need (e.g. data.records:read/write, schema.bases:read), then paste it (starts with pat) as the Bearer secret.",
    docsUrl: "https://support.airtable.com/docs/using-the-airtable-mcp-server",
  },
  {
    id: "neon",
    title: "Neon",
    shortDescription:
      "Create projects, run SQL, manage branches, and inspect Postgres on Neon.",
    category: "data",
    tags: ["postgres", "branches", "sql"],
    featured: false,
    integrations: ["Postgres", "Branches", "SQL"],
    prefilled: {
      name: "Neon",
      url: "https://mcp.neon.tech/mcp",
      headers: [{ name: "Authorization", valuePrefix: "Bearer ", secret: true }],
    },
    authNote:
      "Create a Neon API key (console.neon.tech → Account settings → API keys), then paste it as the Bearer secret. Neon's hosted MCP accepts API key auth on the /mcp endpoint.",
    docsUrl: "https://neon.com/docs/ai/neon-mcp-server",
  },
  {
    id: "jina",
    title: "Jina",
    shortDescription:
      "Read URLs, search the web, embed text, and rerank results for agents.",
    category: "data",
    tags: ["reader", "search", "embeddings"],
    featured: false,
    integrations: ["Reader", "Search", "Embeddings"],
    prefilled: {
      name: "Jina",
      url: "https://mcp.jina.ai/v1",
      headers: [{ name: "Authorization", valuePrefix: "Bearer ", secret: true }],
    },
    authNote:
      "Paste a Jina API key (jina.ai, starts with jina_) as the Bearer secret for search, embedding, and rerank tools. Read-only URL tools work without a key but are rate-limited.",
    docsUrl: "https://github.com/jina-ai/MCP",
  },
  {
    id: "deepwiki",
    title: "DeepWiki",
    shortDescription:
      "Ask questions and read AI-generated docs for public GitHub repositories.",
    category: "development",
    tags: ["docs", "github", "codebase"],
    featured: false,
    integrations: ["Repo docs", "Q&A", "Structure"],
    prefilled: {
      name: "DeepWiki",
      url: "https://mcp.deepwiki.com/mcp",
      headers: [],
    },
    authNote:
      "No authentication is required for public GitHub repos. For private repos, use Devin's MCP at mcp.devin.ai with a Devin API key instead.",
    docsUrl: "https://docs.devin.ai/work-with-devin/deepwiki-mcp",
  },
  {
    id: "langsmith",
    title: "LangSmith",
    shortDescription:
      "Inspect traces, prompts, datasets, and experiments from LangSmith.",
    category: "development",
    tags: ["tracing", "prompts", "evals"],
    featured: false,
    integrations: ["Traces", "Prompts", "Datasets"],
    prefilled: {
      name: "LangSmith",
      url: "https://langsmith-mcp-server.onrender.com/mcp",
      headers: [{ name: "LANGSMITH-API-KEY", valuePrefix: "", secret: true }],
    },
    authNote:
      "Paste a LangSmith personal API key (smith.langchain.com → Settings → API keys, starts with lsv2_pt_) in the LANGSMITH-API-KEY header. LangSmith Cloud also offers OAuth at api.smith.langchain.com/mcp, which this form does not configure.",
    docsUrl: "https://docs.langchain.com/langsmith/langsmith-mcp-server",
  },
  {
    id: "semgrep",
    title: "Semgrep",
    shortDescription:
      "Scan code for security vulnerabilities and unsafe patterns as agents write it.",
    category: "development",
    tags: ["security", "SAST", "scanning"],
    featured: false,
    integrations: ["Security scan", "Rules", "Findings"],
    prefilled: {
      name: "Semgrep",
      url: "https://mcp.semgrep.ai/mcp",
      headers: [],
    },
    authNote:
      "Semgrep's hosted MCP at mcp.semgrep.ai requires no authentication. It is experimental and may change without notice.",
    docsUrl: "https://github.com/semgrep/mcp",
  },
  {
    id: "apify",
    title: "Apify",
    shortDescription:
      "Discover and run Apify Actors, browse docs, and pull datasets from the Store.",
    category: "data",
    tags: ["scraping", "actors", "automation"],
    featured: false,
    integrations: ["Actors", "Datasets", "Docs"],
    prefilled: {
      name: "Apify",
      url: "https://mcp.apify.com",
      headers: [{ name: "Authorization", valuePrefix: "Bearer ", secret: true }],
    },
    authNote:
      "Paste an Apify API token (console.apify.com → Settings → Integrations → API & webhooks) as the Bearer secret. Apify's hosted MCP accepts Bearer auth instead of OAuth when you supply the token in the Authorization header.",
    docsUrl: "https://docs.apify.com/platform/integrations/mcp",
  },
  {
    id: "browserbase",
    title: "Browserbase",
    shortDescription:
      "Navigate, act on, observe, and extract data from live web pages in the cloud.",
    category: "infrastructure",
    tags: ["browser", "automation", "scraping"],
    featured: false,
    integrations: ["Navigate", "Act", "Extract"],
    prefilled: {
      name: "Browserbase",
      url: "https://mcp.browserbase.com/mcp?browserbaseApiKey=",
      headers: [],
    },
    authNote:
      "Append your Browserbase API key to the URL (…?browserbaseApiKey=YOUR_KEY). Browserbase's hosted MCP authenticates via the query parameter — no header is required. Optional params include modelName and modelApiKey for non-default LLM models.",
    docsUrl: "https://docs.browserbase.com/integrations/mcp/setup",
  },
  {
    id: "search1api",
    title: "Search1API",
    shortDescription:
      "Web search, news, crawling, sitemaps, and multi-source research for agents.",
    category: "data",
    tags: ["web search", "news", "crawl"],
    featured: false,
    integrations: ["Search", "News", "Crawl"],
    prefilled: {
      name: "Search1API",
      url: "https://mcp.search1api.com/mcp",
      headers: [{ name: "Authorization", valuePrefix: "Bearer ", secret: true }],
    },
    authNote:
      "Paste a Search1API key (search1api.com) as the Bearer secret. Search1API's hosted MCP accepts Authorization: Bearer or an apiKey query parameter — prefer the header so the key is not logged in URLs.",
    docsUrl: "https://github.com/fatwang2/search1api-mcp",
  },
  {
    id: "datadog",
    title: "Datadog",
    shortDescription:
      "Search logs, metrics, traces, monitors, dashboards, and incidents in Datadog.",
    category: "infrastructure",
    tags: ["observability", "logs", "metrics"],
    featured: false,
    integrations: ["Logs", "Metrics", "APM"],
    prefilled: {
      name: "Datadog",
      url: "https://mcp.datadoghq.com/api/unstable/mcp-server/mcp",
      headers: [
        { name: "DD_API_KEY", valuePrefix: "", secret: true },
        { name: "DD_APPLICATION_KEY", valuePrefix: "", secret: true },
      ],
    },
    authNote:
      "Create a scoped Datadog API key and application key (Organization Settings → API Keys / Application Keys), then paste them in the DD_API_KEY and DD_APPLICATION_KEY headers. Use https://mcp.datadoghq.eu/… for EU sites (us3, us5, ap1, ap2 have their own mcp.* hostnames). Datadog also supports OAuth, which this form does not configure.",
    docsUrl: "https://docs.datadoghq.com/mcp_server/setup/",
  },
  {
    id: "honeycomb",
    title: "Honeycomb",
    shortDescription:
      "Query datasets, explore traces, and investigate production telemetry with agents.",
    category: "infrastructure",
    tags: ["observability", "traces", "investigation"],
    featured: false,
    integrations: ["Datasets", "Traces", "Boards"],
    prefilled: {
      name: "Honeycomb",
      url: "https://mcp.honeycomb.io/mcp",
      headers: [{ name: "Authorization", valuePrefix: "Bearer ", secret: true }],
    },
    authNote:
      "Create a Management API key (Account → Team Settings → API Keys) with MCP Read scope, then paste KEY_ID:SECRET_KEY (colon-separated, no spaces) as the Bearer secret. EU teams should use https://mcp.eu1.honeycomb.io/mcp instead.",
    docsUrl:
      "https://docs.honeycomb.io/integrations/mcp/configuration-guide",
  },
  {
    id: "zapier",
    title: "Zapier",
    shortDescription:
      "Trigger actions across 8,000+ apps through your own Zapier MCP endpoint.",
    category: "business",
    tags: ["automation", "actions", "8000+ apps"],
    featured: false,
    integrations: ["8,000+ apps", "Actions", "Automation"],
    prefilled: {
      name: "Zapier",
      url: "",
      headers: [],
    },
    authNote:
      "Create a server at mcp.zapier.com, add the actions you want to expose, then copy your unique server URL from the Connect tab and paste it as the URL above. That URL carries its own authentication — treat it like a password and add no header. Rotate it from Zapier if it leaks.",
    docsUrl:
      "https://help.zapier.com/hc/en-us/articles/36265392843917-Use-Zapier-MCP-with-your-client",
  },
  {
    id: "custom",
    title: "Custom server",
    shortDescription:
      "Enter any Streamable HTTP MCP endpoint and configure auth manually.",
    category: "infrastructure",
    tags: ["custom", "self-hosted"],
    featured: false,
    integrations: ["Any MCP server"],
    prefilled: {
      name: "",
      url: "",
      headers: [],
    },
    authNote:
      "Provide the Streamable HTTP URL for your MCP server. Use Bearer auth for token-based servers or Custom header when the provider expects a named API key header.",
    docsUrl: "https://modelcontextprotocol.io",
  },
];

export function searchMcpServerTemplates(query, category = "all") {
  const lowered = String(query || "")
    .trim()
    .toLowerCase();
  return MCP_SERVER_TEMPLATES.filter((template) => {
    if (category !== "all" && template.category !== category) return false;
    if (!lowered) return true;
    return (
      template.title?.toLowerCase().includes(lowered) ||
      template.shortDescription?.toLowerCase().includes(lowered) ||
      template.category?.toLowerCase().includes(lowered) ||
      (template.tags || []).some((tag) => tag.toLowerCase().includes(lowered)) ||
      (template.integrations || []).some((item) =>
        item.toLowerCase().includes(lowered)
      )
    );
  });
}

export function getMcpServerTemplate(id) {
  return MCP_SERVER_TEMPLATES.find((template) => template.id === id) || null;
}

/** Infer template brand id from a saved server name/URL for card previews. */
export function resolveMcpServerBrandId(server) {
  const url = String(server?.url || "").trim().toLowerCase();
  const name = String(server?.name || "").trim().toLowerCase();

  for (const template of MCP_SERVER_TEMPLATES) {
    if (template.id === "custom") continue;

    const templateUrl = String(template.prefilled?.url || "")
      .trim()
      .toLowerCase();
    if (templateUrl && url) {
      try {
        const serverHost = new URL(url).hostname;
        const templateHost = new URL(templateUrl).hostname;
        if (serverHost === templateHost) return template.id;
      } catch (_e) {
        if (url.startsWith(templateUrl)) return template.id;
      }
    }

    const templateName = String(template.title || template.id || "")
      .trim()
      .toLowerCase();
    if (name && templateName && name === templateName) return template.id;
  }

  return "custom";
}

export function applyMcpServerTemplate(template) {
  const prefilled = template?.prefilled || {};
  const headers = (prefilled.headers || []).map(headerViewToRow);
  return {
    name: prefilled.name || "",
    url: prefilled.url || "",
    headers,
    authMode: template?.authMode || "static",
    requiresClient: Boolean(template?.requiresClient),
  };
}
